import { Link } from 'react-router-dom'
import {Building2, Factory, ShoppingBag, Home, Hotel, ArrowRight} from 'lucide-react'
import SectionHeading from '@/components/SectionHeading'

const industries = [
  {
    icon: Home,
    title: 'Strata & Residential Buildings',
    description: 'Comprehensive programs for condos, co-ops, and residential towers — concierge, janitorial, maintenance, and 24/7 response, all coordinated through a single account manager.',
    highlights: ['Resident-facing concierge', 'Common area cleaning', 'Emergency maintenance', 'Strata council reporting'],
    image: 'https://images.pexels.com/photos/323705/pexels-photo-323705.jpeg?auto=compress&cs=tinysrgb&w=1200',
  },
  {
    icon: Building2,
    title: 'Commercial Offices',
    description: 'From boutique suites to Class A towers — consistent janitorial, day porters, and building maintenance designed around tenant experience and corporate standards.',
    highlights: ['Day & evening shifts', 'LEED-compatible programs', 'After-hours flexibility', 'Tenant satisfaction metrics'],
    image: 'https://images.pexels.com/photos/269077/pexels-photo-269077.jpeg?auto=compress&cs=tinysrgb&w=1200',
  },
  {
    icon: ShoppingBag,
    title: 'Retail & Shopping Centres',
    description: 'High-traffic retail demands visible cleanliness at every hour. Our day porter teams and overnight programs keep shopping centres sharp and welcoming.',
    highlights: ['Peak-hour porters', 'Food court programs', 'Restroom attendants', 'Seasonal deep cleans'],
    image: 'https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg?auto=compress&cs=tinysrgb&w=1200',
  },
  {
    icon: Factory,
    title: 'Industrial & Warehouses',
    description: 'Industrial-grade cleaning, floor care, and maintenance for distribution centres, manufacturing, and logistics operations where safety and uptime are non-negotiable.',
    highlights: ['Forklift-safe protocols', 'High-bay cleaning', 'Dock & loading care', 'OSHA-compliant teams'],
    image: 'https://images.pexels.com/photos/236705/pexels-photo-236705.jpeg?auto=compress&cs=tinysrgb&w=1200',
  },
  {
    icon: Hotel,
    title: 'Hospitality',
    description: 'Hotels, resorts, and event venues trust Delta to protect guest experience with hospitality-trained teams and discrete, always-on service.',
    highlights: ['Brand-aligned uniforms', 'Event support staff', 'Back-of-house programs', 'Premium surface care'],
    image: 'https://images.pexels.com/photos/258154/pexels-photo-258154.jpeg?auto=compress&cs=tinysrgb&w=1200',
  },
]

function Industries() {
  return (
    <div>
      {/* HERO */}
      <section className="bg-grey-soft py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-orange-accent" />
              <span className="text-xs uppercase tracking-[0.3em] text-orange-accent font-semibold">Industries We Serve</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] text-navy">
              Every environment. <span className="text-orange-accent">One standard.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted leading-relaxed">
              Purpose-built maintenance programs tailored to the distinct needs of each industry we serve.
            </p>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-24">
          {industries.map((ind, i) => {
            const Icon = ind.icon
            const reversed = i % 2 === 1
            return (
              <div key={ind.title} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${reversed ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                <div>
                  <div className="w-14 h-14 bg-orange-accent rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-navy leading-tight">{ind.title}</h2>
                  <p className="mt-5 text-muted leading-relaxed text-lg">{ind.description}</p>
                  <ul className="mt-7 space-y-2.5">
                    {ind.highlights.map((h) => (
                      <li key={h} className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-accent" />
                        <span className="text-navy font-medium text-[15px]">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img src={ind.image} alt={ind.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" />
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy text-white">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
            Your industry deserves a specialist.
          </h2>
          <p className="mt-5 text-white/75 text-lg">
            We'll build a program specifically for your property type and operational rhythm.
          </p>
          <Link
            to="/quote"
            className="mt-8 inline-flex items-center gap-2 bg-orange-accent hover:bg-orange-dark text-white px-8 py-4 rounded-md font-semibold transition-all group shadow-lg shadow-orange-accent/40"
          >
            Request a Quote <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default Industries
